import sectionType from '../utils/Enum/sectionType';

// Mock test report data generator for testing purposes
export const generateMockTestReport = (userId, overrideData = {}) => {
    const mathSections = [
        sectionType.ALGEBRA,
        sectionType.ADVANCED_MATH,
        sectionType.PROBLEM_SOLVING_AND_DATA_ANALYSIS,
        sectionType.GEOMETRY_AND_TRIGONOMETRY
    ];

    const readingWritingSections = [
        sectionType.INFORMATION_AND_IDEAS,
        sectionType.CRAFT_AND_STRUCTURE,
        sectionType.EXPRESSION_OF_IDEAS,
        sectionType.STANDARD_ENGLISH_CONVENTIONS
    ];

    // Generate random scores for each section
    const generateSectionScore = (minCorrect = 8, maxCorrect = 15, total = 15) => {
        const correct = Math.floor(Math.random() * (maxCorrect - minCorrect + 1)) + minCorrect;
        const percentage = Math.round((correct / total) * 100);
        return { correct, total, percentage };
    };

    // Create section scores map
    const sectionScores = {};

    // Math section scores (11 questions each, 44 total)
    // 4 domains × 11 questions = 44 questions
    mathSections.forEach(section => {
        sectionScores[section] = generateSectionScore(6, 11, 11);
    });

    // Reading & Writing section scores (13-14 questions each, 54 total)
    // SAT Digital: 54 questions total split across 4 skill domains
    // Distribution: 14 + 13 + 14 + 13 = 54 questions
    readingWritingSections.forEach((section, index) => {
        const questionsPerSection = (index % 2 === 0) ? 14 : 13;
        const minCorrect = Math.floor(questionsPerSection * 0.5);
        sectionScores[section] = generateSectionScore(minCorrect, questionsPerSection, questionsPerSection);
    });

    // Calculate totals
    let totalCorrect = 0;
    let totalQuestions = 0;
    let mathCorrect = 0;
    let mathTotal = 0;
    let rwCorrect = 0;
    let rwTotal = 0;

    Object.entries(sectionScores).forEach(([section, scores]) => {
        totalCorrect += scores.correct;
        totalQuestions += scores.total;

        if (mathSections.includes(section)) {
            mathCorrect += scores.correct;
            mathTotal += scores.total;
        } else {
            rwCorrect += scores.correct;
            rwTotal += scores.total;
        }
    });

    const overallPercentage = Math.round((totalCorrect / totalQuestions) * 100);
    const mathPercentage = Math.round((mathCorrect / mathTotal) * 100);
    const rwPercentage = Math.round((rwCorrect / rwTotal) * 100);

    const baseReport = {
        _id: `mock_report_${Date.now()}`,
        userId: userId,
        testType: 'SAT_FULL_TEST',
        testDate: new Date().toISOString(),
        totalQuestions,
        totalCorrect,
        overallPercentage,
        timeSpent: Math.floor(Math.random() * 30) + 120, // 120-150 minutes
        sectionScores,
        mathScore: {
            correct: mathCorrect,
            total: mathTotal,
            percentage: mathPercentage
        },
        readingWritingScore: {
            correct: rwCorrect,
            total: rwTotal,
            percentage: rwPercentage
        },
        completedSections: [...mathSections, ...readingWritingSections],
        isCompleted: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    };

    return { ...baseReport, ...overrideData };
};

// Generate multiple mock reports for testing
export const generateMockTestReports = (userId, count = 5) => {
    const reports = [];

    for (let i = 0; i < count; i++) {
        const testDate = new Date();
        testDate.setDate(testDate.getDate() - (i * 7)); // One report per week going back

        reports.push(generateMockTestReport(userId, {
            testDate: testDate.toISOString(),
            createdAt: testDate.toISOString(),
            updatedAt: testDate.toISOString()
        }));
    }

    return reports.sort((a, b) => new Date(b.testDate) - new Date(a.testDate)); // Most recent first
};

// Sample high-performing test report
export const generateHighScoreReport = (userId) => {
    return generateMockTestReport(userId, {
        overallPercentage: 92,
        totalCorrect: 98,
        totalQuestions: 98,
        mathScore: {
            correct: 40,
            total: 44,
            percentage: 91
        },
        readingWritingScore: {
            correct: 58,
            total: 64,
            percentage: 91
        },
        sectionScores: {
            [sectionType.ALGEBRA]: { correct: 10, total: 11, percentage: 91 },
            [sectionType.ADVANCED_MATH]: { correct: 10, total: 11, percentage: 91 },
            [sectionType.PROBLEM_SOLVING_AND_DATA_ANALYSIS]: { correct: 10, total: 11, percentage: 91 },
            [sectionType.GEOMETRY_AND_TRIGONOMETRY]: { correct: 10, total: 11, percentage: 91 },
            [sectionType.INFORMATION_AND_IDEAS]: { correct: 15, total: 16, percentage: 94 },
            [sectionType.CRAFT_AND_STRUCTURE]: { correct: 14, total: 16, percentage: 88 },
            [sectionType.EXPRESSION_OF_IDEAS]: { correct: 15, total: 16, percentage: 94 },
            [sectionType.STANDARD_ENGLISH_CONVENTIONS]: { correct: 14, total: 16, percentage: 88 }
        }
    });
};

// Sample low-performing test report
export const generateLowScoreReport = (userId) => {
    return generateMockTestReport(userId, {
        overallPercentage: 45,
        totalCorrect: 49,
        totalQuestions: 98,
        mathScore: {
            correct: 18,
            total: 44,
            percentage: 41
        },
        readingWritingScore: {
            correct: 31,
            total: 64,
            percentage: 48
        },
        sectionScores: {
            [sectionType.ALGEBRA]: { correct: 4, total: 11, percentage: 36 },
            [sectionType.ADVANCED_MATH]: { correct: 5, total: 11, percentage: 45 },
            [sectionType.PROBLEM_SOLVING_AND_DATA_ANALYSIS]: { correct: 4, total: 11, percentage: 36 },
            [sectionType.GEOMETRY_AND_TRIGONOMETRY]: { correct: 5, total: 11, percentage: 45 },
            [sectionType.INFORMATION_AND_IDEAS]: { correct: 8, total: 16, percentage: 50 },
            [sectionType.CRAFT_AND_STRUCTURE]: { correct: 7, total: 16, percentage: 44 },
            [sectionType.EXPRESSION_OF_IDEAS]: { correct: 8, total: 16, percentage: 50 },
            [sectionType.STANDARD_ENGLISH_CONVENTIONS]: { correct: 8, total: 16, percentage: 50 }
        }
    });
};